import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Globe, Building2, Edit, Shield, Clock, DollarSign, Calendar, Calculator, ChevronRight , Scale} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function CountryLawsPage() {
  const [countries, setCountries] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showBranchDialog, setShowBranchDialog] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [eosCalc, setEosCalc] = useState({ country_code: 'SA', monthly_salary: '', service_years: '', exit_type: 'resignation' });
  const [eosResult, setEosResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [countriesRes, branchesRes] = await Promise.all([
        api.get('/country-laws'),
        api.get('/branches'),
      ]);
      setCountries(countriesRes.data);
      setBranches(branchesRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleAssignCountry = async (branchId, countryCode) => {
    try {
      await api.put(`/branches/${branchId}/country`, { country_code: countryCode });
      toast.success('Country assigned to branch');
      fetchData();
      setShowBranchDialog(false);
    } catch (err) {
      toast.error('Failed to assign country');
    }
  };

  const handleCalculateEOS = async () => {
    try {
      const res = await api.post('/calculate-eos', eosCalc);
      setEosResult(res.data);
    } catch (err) {
      toast.error('Calculation failed');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="country-laws-page">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-4">

              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-200/50">

                <Scale size={24} className="text-white" />

              </div>

              <div>

                <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="page-title">Country Labor Laws</h1>

                <p className="text-sm text-muted-foreground mt-0.5">Labor law compliance reference</p>

              </div>

            </div>
            <p className="text-muted-foreground text-sm mt-1">Configure labor laws per country. Assign countries to branches for automatic compliance.</p>
          </div>
        </div>

        <Tabs defaultValue="countries" className="space-y-4">
          <TabsList data-testid="laws-tabs">
            <TabsTrigger value="countries" data-testid="tab-countries">Countries</TabsTrigger>
            <TabsTrigger value="branches" data-testid="tab-branches">Branch Assignment</TabsTrigger>
            <TabsTrigger value="calculator" data-testid="tab-calculator">EOS Calculator</TabsTrigger>
          </TabsList>

          <TabsContent value="countries" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {countries.map((c) => (
                <Card
                  key={c.country_code}
                  className={`cursor-pointer transition-all hover:shadow-md ${selectedCountry?.country_code === c.country_code ? 'ring-2 ring-primary' : ''}`}
                  onClick={() => setSelectedCountry(c)}
                  data-testid={`country-card-${c.country_code}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Globe size={16} className="text-primary" />
                        {c.country_name}
                      </CardTitle>
                      <Badge variant={c.is_customized ? 'default' : 'secondary'} className="text-xs">
                        {c.is_customized ? 'Customized' : 'Default'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="text-xs text-muted-foreground space-y-1.5">
                    <div className="flex justify-between"><span>Currency</span><span className="font-medium text-foreground">{c.currency}</span></div>
                    <div className="flex justify-between"><span>Work Hours/Day</span><span className="font-medium text-foreground">{c.working_hours_per_day}h</span></div>
                    <div className="flex justify-between"><span>Work Days/Week</span><span className="font-medium text-foreground">{c.working_days_per_week}d</span></div>
                    <div className="flex justify-between"><span>Overtime Rate</span><span className="font-medium text-foreground">{c.overtime_rate}x</span></div>
                    <div className="flex justify-between"><span>Annual Leave</span><span className="font-medium text-foreground">{c.leave_policies?.annual?.days || '-'} days</span></div>
                    <div className="flex justify-between"><span>Notice Period</span><span className="font-medium text-foreground">{c.notice_period_days} days</span></div>
                    {c.wps_enabled && <Badge variant="outline" className="text-[10px] w-fit">WPS Enabled</Badge>}
                  </CardContent>
                </Card>
              ))}
            </div>

            {selectedCountry && (
              <Card className="mt-4" data-testid="country-detail">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Globe size={18} className="text-primary" /> {selectedCountry.country_name} - Detailed Configuration
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="leaves" className="space-y-3">
                    <TabsList>
                      <TabsTrigger value="leaves">Leave Policies</TabsTrigger>
                      <TabsTrigger value="insurance">Social Insurance</TabsTrigger>
                      <TabsTrigger value="eos">End of Service</TabsTrigger>
                      <TabsTrigger value="salary">Salary Structure</TabsTrigger>
                    </TabsList>
                    <TabsContent value="leaves">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(selectedCountry.leave_policies || {}).map(([type, config]) => (
                          <div key={type} className="bg-muted/50 rounded-lg p-3 border">
                            <div className="font-medium text-sm text-foreground capitalize mb-1.5">{type.replace(/_/g, ' ')}</div>
                            <div className="space-y-1 text-xs text-muted-foreground">
                              {Object.entries(config).map(([k, v]) => (
                                <div key={k} className="flex justify-between">
                                  <span className="capitalize">{k.replace(/_/g, ' ')}</span>
                                  <span className="font-medium text-foreground">{typeof v === 'boolean' ? (v ? 'Yes' : 'No') : v}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </TabsContent>
                    <TabsContent value="insurance">
                      <div className="bg-muted/50 rounded-lg p-4 border space-y-2">
                        {Object.entries(selectedCountry.social_insurance || {}).map(([k, v]) => (
                          <div key={k} className="flex justify-between text-sm">
                            <span className="text-muted-foreground capitalize">{k.replace(/_/g, ' ')}</span>
                            <span className="font-medium text-foreground">{typeof v === 'number' && v < 1 ? `${(v * 100).toFixed(2)}%` : String(v)}</span>
                          </div>
                        ))}
                      </div>
                    </TabsContent>
                    <TabsContent value="eos">
                      <div className="bg-muted/50 rounded-lg p-4 border space-y-2">
                        {Object.entries(selectedCountry.eos_rules || {}).map(([k, v]) => (
                          <div key={k} className="text-sm">
                            <span className="font-medium text-foreground capitalize">{k.replace(/_/g, ' ')}: </span>
                            {typeof v === 'object' ? (
                              <div className="ml-4 mt-1 space-y-1">
                                {Object.entries(v).map(([sk, sv]) => (
                                  <div key={sk} className="flex gap-2 text-xs text-muted-foreground">
                                    <span className="capitalize">{sk.replace(/_/g, ' ')}:</span>
                                    <span className="font-medium">{sv}</span>
                                  </div>
                                ))}
                              </div>
                            ) : <span className="text-muted-foreground">{v}</span>}
                          </div>
                        ))}
                      </div>
                    </TabsContent>
                    <TabsContent value="salary">
                      <div className="bg-muted/50 rounded-lg p-4 border space-y-2">
                        {Object.entries(selectedCountry.salary_structure || {}).map(([k, v]) => (
                          <div key={k} className="flex justify-between text-sm">
                            <span className="text-muted-foreground capitalize">{k.replace(/_/g, ' ')}</span>
                            <span className="font-medium text-foreground">{(v * 100).toFixed(0)}%</span>
                          </div>
                        ))}
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="branches" className="space-y-4">
            <p className="text-sm text-muted-foreground">Assign a country to each branch. The labor law for that country will automatically apply to employees in that branch.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {branches.map((b) => (
                <Card key={b.id} data-testid={`branch-card-${b.id}`}>
                  <CardContent className="flex items-center justify-between py-4">
                    <div className="flex items-center gap-3">
                      <Building2 size={18} className="text-muted-foreground" />
                      <div>
                        <div className="font-medium text-sm">{b.name}</div>
                        <div className="text-xs text-muted-foreground">
                          Current: <Badge variant="outline" className="text-[10px] ml-1">
                            {countries.find(c => c.country_code === b.country_code)?.country_name || b.country_code || 'Not Set'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <Select
                      value={b.country_code || ''}
                      onValueChange={(val) => handleAssignCountry(b.id, val)}
                    >
                      <SelectTrigger className="w-48" data-testid={`branch-country-select-${b.id}`}>
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((c) => (
                          <SelectItem key={c.country_code} value={c.country_code}>
                            {c.country_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="calculator" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Calculator size={16} className="text-primary" /> End-of-Service Benefits Calculator</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Country</Label>
                    <Select value={eosCalc.country_code} onValueChange={(v) => setEosCalc({ ...eosCalc, country_code: v })}>
                      <SelectTrigger data-testid="eos-country-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {countries.map((c) => <SelectItem key={c.country_code} value={c.country_code}>{c.country_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Monthly Salary</Label>
                    <Input type="number" value={eosCalc.monthly_salary} onChange={(e) => setEosCalc({ ...eosCalc, monthly_salary: e.target.value })} placeholder="e.g. 5000" data-testid="eos-salary-input" />
                  </div>
                  <div>
                    <Label>Service Years</Label>
                    <Input type="number" step="0.5" value={eosCalc.service_years} onChange={(e) => setEosCalc({ ...eosCalc, service_years: e.target.value })} placeholder="e.g. 3.5" data-testid="eos-years-input" />
                  </div>
                  <div>
                    <Label>Exit Type</Label>
                    <Select value={eosCalc.exit_type} onValueChange={(v) => setEosCalc({ ...eosCalc, exit_type: v })}>
                      <SelectTrigger data-testid="eos-exit-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="resignation">Resignation</SelectItem>
                        <SelectItem value="termination">Termination</SelectItem>
                        <SelectItem value="end_of_contract">End of Contract</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={handleCalculateEOS} data-testid="eos-calculate-btn">Calculate EOS</Button>
                {eosResult && (
                  <div className="bg-success/10 border border-success/30 rounded-lg p-4 mt-4" data-testid="eos-result">
                    <div className="text-sm text-muted-foreground">Country: <span className="font-medium">{eosResult.country}</span></div>
                    <div className="text-sm text-muted-foreground">Exit Type: <span className="font-medium capitalize">{eosResult.exit_type}</span></div>
                    <div className="text-sm text-muted-foreground">Basis: <span className="font-medium">{eosResult.calculation_basis}</span></div>
                    <div className="text-2xl font-bold text-success mt-2">{eosResult.eos_amount?.toLocaleString()} {countries.find(c => c.country_name === eosResult.country)?.currency || ''}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
